<template>
  <div class="flex justify-content-center" style="min-width: 600px; max-height: 600px; overflow-y: auto">
    <Stepper>
      <template v-if="slideDesign" v-for="(steps, index) in slideDesign.steps" :key="index">
        <StepperPanel>
          <template #content="{ prevCallback, nextCallback }">

              <div v-if="props.slide.name === 'Benefits'">
                <h5>Choose Benefit {{ index + 1 }}</h5>
                <div v-for="(benefit, index) in sortedBenefits" :key="index">
                  <RadioButton
                      :inputId="'benefit' + index"
                      name="spot1"
                      :value="benefit.json.BenefitGuid"
                      style="margin-right:10px;"
                      v-model="chosenBenefits"
                  />
                  <label :for="'benefit' + index">{{ benefit.json.Name }}</label>
                </div>
            </div>

            <div v-else>
              <div v-for="(widgetIds, index) in steps">
                <div v-for="(widgetId, index) in widgetIds">
                  <div v-if="getWidget(widgetId.id).subType === 'model_text'">
                    <h5>Choose Model Text Value {{ index + 1 }}</h5>
                    <ValueModelTextAutoComplete @item-selected="chooseValueModelData($event, widgetId.id)" :wizard="true" :wizard-hook-id="getWidgetHookId(widgetId.id)" />
                  </div>

                  <div v-if="getWidget(widgetId.id).subType === 'out_text'">
                    <h5>Choose Output Value {{ index + 1 }}</h5>
                    <ValueModelTextAutoComplete @item-selected="chooseValueModelData($event, widgetId.id)" :wizard="true" :wizard-hook-id="getWidgetHookId(widgetId.id)" />
                  </div>

                  <div v-if="getWidget(widgetId.id).subType === 'choice'">
                    <h5>Choose Choice Variable {{ index + 1 }}</h5>
                    <ValueModelTextAutoComplete @item-selected="chooseValueModelData($event, widgetId.id)" :wizard="true" :wizard-hook-id="getWidgetHookId(widgetId.id)" />
                  </div>

                  <div v-if="getWidget(widgetId.id).subType === 'input_number'">
                    <h5>Choose Input Number {{ index + 1 }}</h5>
                    <ValueModelTextAutoComplete @item-selected="chooseValueModelData($event, widgetId.id)" :wizard="true" :wizard-hook-id="getWidgetHookId(widgetId.id)" />
                  </div>
                </div>
              </div>
            </div>

            <div style="margin-top: 40px;">
              <div style="display:flex;justify-content:space-between;" v-if="index !== 0 && index !== slideDesign.steps.length - 1">
                <Button label="Back" @click="() => { prevCallback(); prevStep(); }"><IconArrowLeft/></Button>
                <Button label="Next" @click="() => { nextCallback(); nextStep(); }"><IconArrowRight/></Button>
              </div>
              <div style="display:flex;justify-content: end" v-if="index === 0">
                <Button label="Next" @click="() => { nextCallback(); nextStep(); }"><IconArrowRight/></Button>
              </div>
              <div class="end" style="display:flex;justify-content: start" v-if="index === slideDesign.steps.length - 1">
                <Button label="Back" @click="() => { prevCallback(); prevStep(); }"><IconArrowLeft/></Button>
              </div>
            </div>
          </template>
        </StepperPanel>
      </template>
    </Stepper>
  </div>
  <div v-if="props.slide.name === 'Image' || props.slide.name === 'Section' || props.slide.name === 'Two Column Marketing'">
    <FileManager @fileChosen="fileChosen"></FileManager>
  </div>
</template>

<script setup lang="ts">
import {ref, onBeforeMount, computed} from "vue";
import FileManager from "@/views/components/element/FileManagerElement/index.vue";
import {useSlidesStore, useGivingTreeStore} from "@/store";
import useMicroservice from "@/hooks/useMicroservice";
import {SlideBackground, ChartData} from "@/types/slides";
import {storeToRefs} from "pinia";
import {slideDesigns} from "@/mocks/slideDesigns";
import ValueModelTextAutoComplete from "@/views/Editor/CanvasTool/components/ValueModelTextAutoComplete.vue";
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";

const slidesStore = useSlidesStore()
const givingTreeStore = useGivingTreeStore()
const { benefits } = storeToRefs(givingTreeStore)
const api = useMicroservice()

const {getActiveHookId, getComputedValue} = useComputedTreeValue()

const props = defineProps({
  slide: {
    type: Object,
    required: true
  }
})

const activeIndex = ref(0);
const widgets: any = ref([])
const slideDesign = computed(() => slideDesigns.find(slide => slide.slideType === props.slide.name))

const emit = defineEmits(['closeDialog']);

onBeforeMount(() => {

  if (slidesStore.currentSlide.elements.length !== 0) extractWidgets()
  refreshBackground()

  if (props.slide.name === 'Benefits') setUpBenefitsSlide()

  if (props.slide.name === 'Total Cost of Ownership') setUpTCOSlide()
})

const refreshBackground = () => {
  const background: SlideBackground = {
    type: 'solid',
    color: '#fff',
  }
  slidesStore.updateSlide({ background })
  api.savePresentation()
}

const extractWidgets = () => {
  slidesStore.currentSlide.elements.forEach((element: any) => {
    if (element.type === 'dragdrop') {
      element.widgets.forEach((widget: any) => {
        widget.children.forEach((child: any) => {
          widgets.value.push({ dragdropId: element.id, parentId: widget.parent, id: child.id, type: child.type })
        })
      })
    }
  })
}

const getWidget = (widgetId: string) => widgets.value.find((item: any) => item.id === widgetId);

const findElement = (widget: any) => {
  const elements = slidesStore.currentSlide.elements
  const slideElement = elements.find(item => item.id === widget.dragdropId)
  if (!slideElement) return null
  const widgetElement = slideElement.widgets.find((item: any) => item.parent === widget.parentId)
  if (!widgetElement) return null
  return widgetElement.children.find((item: any) => item.id === widget.id)
};

const getFirstWidgetHookId = () => {
  const firstWidgetId = slideDesign.value.steps[activeIndex.value].widgetIds[0];
  const widget = getWidget(firstWidgetId.id);
  const element = findElement(widget);
  return element ? element.hookId : null;
};

const getWidgetHookId = (widgetId: string) => {
  const widget = getWidget(widgetId);
  const element = findElement(widget);
  return element ? element.hookId : '';
};

const updateAllWidgetHookIds = (val: any) => {
  updateWidgetHookIds(() => val);
};

const updateWidgetHookIds = (callback: any) => {
  const widgetIds = slideDesign.value.steps[activeIndex.value].widgetIds;
  widgetIds.forEach((widgetId, index) => {
    const widget = getWidget(widgetId.id);
    const element = findElement(widget);
    if (element) {
      element.hookId = callback(widget, index);
    }
  });
};

const updateWidget = (text: string, hookId: string, widgetId: string) => {
  const widget = getWidget(widgetId);
  const element = findElement(widget);
  if (element) {
    element.text = text
    element.hookId = hookId
  }
}

const updateSlideAndSave = () => {
  slidesStore.updateSlide({ elements: slidesStore.currentSlide.elements })
  api.savePresentation()
};

const nextStep = () => {
  if (activeIndex.value < slideDesign.value.steps.length - 1) {
    activeIndex.value++;
  }
};

const prevStep = () => {
  if (activeIndex.value > 0) {
    activeIndex.value--;
  }
};

const fileChosen = (file) => {
  if (props.slide.name === 'Image') setBackgroundImage(file.resultsThumbnailImg)

  if (props.slide.name === 'Section' || props.slide.name === 'Two Column Marketing') setWidgetImage(file.resultsThumbnailImg)
}

const setBackgroundImage = (imageUrl: string) => {
  const background: SlideBackground = {
    ...slidesStore.currentSlide.background,
    type: 'image',
    image: imageUrl,
    imageSize: 'cover',
  }
  slidesStore.updateSlide({ background, elements: [] })
  api.savePresentation()
  emit("closeDialog")
}

const setWidgetImage = (imageUrl: string) => {
  const imageWidget = widgets.value.find((item:any) => item.type === 'image')
  if (imageWidget) {
    const element = findElement(imageWidget);
    if (element) {
      element.imageUrl = imageUrl;
      updateSlideAndSave();
      emit("closeDialog");
    }
  }
}

/* Benefits */
const sortBenefits = () => {
  const benefitsArray = Object.values(benefits.value)
  return benefitsArray.sort((a: any, b: any) => a.order - b.order)
}

const sortedBenefits = ref(sortBenefits())

const chosenBenefits = computed({
  get() {
    return getFirstWidgetHookId();
  },
  set(val) {
    updateAllWidgetHookIds(val);
    updateSlideAndSave();
  }
});

const setUpBenefitsSlide = () => {
  if (Object.keys(benefits.value).length <= 3) {
    updateWidgetHookIds((_: any, index: number) => sortedBenefits.value[index].json.BenefitGuid);
    updateSlideAndSave();
    emit("closeDialog")
  }
}

/* TCO */
const setUpTCOSlide = () => {
  //TODO: WIP - add hookIds
  const chartWidget = widgets.value.find((item: any) => item.type === 'chart')
  const variables = ['compPricePlusDiffValue', 'ourPrice', 'valueToCustomer']
  const computedValues = []

  variables.forEach(variable => {
    getActiveHookId(variable)

    const computedValue = getComputedValue.value
    computedValues.push(computedValue)
  })

  let data: ChartData = {
    labels: ['Comp Price', 'Our Price & Value to You'],
    legends: ['Series 1', 'Series 2'],
    series: [
      [0, 0],
      [0, 0]
    ]
  }

  data.series[0][0] = {meta: 'Competitor Price', value: computedValues[0]}
  data.series[0][1] = {meta: 'Our Price', value: computedValues[1]}


  data.series[1][0] = {meta: '', value: 0}
  data.series[1][1] = {meta: 'Value to You', value: computedValues[2]}

  const widget = getWidget(chartWidget.id);
  const element = findElement(widget);
  element.data = data
  updateSlideAndSave()
}

const chooseValueModelData = (event: any, widgetId: string) => {
  updateWidget(event.value.label, event.value.hookId, widgetId)
  updateSlideAndSave()
}

</script>

<style scoped lang="scss">
:deep(ul) {
  padding-right: 2rem;
}
:deep(.p-stepper .p-stepper-separator) {
  width: 50px;
}
:deep(.p-button.button-types) {
  border: 0;
}
</style>